import React, { type ChangeEvent, type ReactElement } from 'react';
import { Icon, InlineMessage, InputField } from '@saviynt/design-system';
import cx from 'classnames';

import { FieldLabel } from '../../../../shared/components';
import { testIds, VALIDATION_TYPES } from '../../../../shared/constants';
import { type TextareaField } from '../../../../shared/types';
import { HELPTEXT_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';

import styles from './PreviewFields.module.css';

interface TextAreaInputProps {
  field: TextareaField;
  value: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
}

function TextAreaInput({
  field,
  value,
  onChange,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
}: TextAreaInputProps): ReactElement | null {
  const {
    fieldName,
    label,
    placeholder,
    isReadOnly: staticReadOnly,
    validations,
    infoTooltipText,
    helperText,
    lineCount,
    isHidden,
    defaultValue,
  } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const isReadOnly = staticReadOnly || isReadOnlyProp || false;

  if (isHidden) {
    return null;
  }

  return (
    <InputField
      name={fieldName}
      kind='multiline'
      label={
        <FieldLabel
          label={label}
          htmlFor={fieldName}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
        />
      }
      placeholder={placeholder ?? ''}
      value={value ?? defaultValue?.value ?? ''}
      onChange={isReadOnly ? () => {} : onChange}
      isRequired={isRequired}
      isDisabled={isReadOnly}
      isCritical={!!error}
      CriticalHelperText={
        <InlineMessage
          text={error}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      }
      HelperText={
        helperText ? (
          <InlineMessage
            text={helperText.slice(0, HELPTEXT_VALIDATION.MAX_LENGTH)}
            colorTheme='neutral'
            size='small'
          />
        ) : undefined
      }
      rows={Math.max(2, Math.min(lineCount || 4, 20))}
      className={cx(isReadOnly && styles.value__isReadOnly)}
      dataTestId={testIds.FF_PREVIEW_TEXT_AREA_FIELD}
    />
  );
}

export default TextAreaInput;
